// Native UI for the data-sources-registry tool. DSR 1.2.0 —
// renders ONE TAB PER REGISTERED REGISTRY FILE (storage.registryFiles
// in the tool manifest; legacy single-registryPath configs get one
// tab). Each tab loads its file from the tool's bound container and
// dispatches table rendering by the file's declared schema:
//   - data-sources-v1 → <DataSourcesTable/> (KPIs / filters / table /
//     add-entry form — the pre-1.2.0 page body, extracted)
//   - bookmarks-v1    → <BookmarksTable/> (title/url/captured/source/
//     tags/note table over registry/bookmarks.yaml)
//
// Replicates the standalone code-tron/data-sources-registry/registry.html
// inside captain-tasko's chrome. Differences from the standalone:
//   - YAML lives in the bound container (one source of truth — git-
//     versioned vs in-app edits both end up here)
//   - Add-entry writes back to the document via the documents API,
//     not just generating a paste-ready YAML block
//   - KPI hues + chip styling track captain-tasko's design tokens
//
// Auth: requires tools:read_registry to view (the bound document
// itself respects org scoping via the documents API; this page just
// fetches via /tools/:id/registry?path=<file> → contentUrl).

import { useEffect, useMemo, useState } from 'react';
import { Brush } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { resolveRegistryFiles } from '@captain-tasko/shared';
import type { RegistryFileEntry } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { TabHeaderCanonicalLink } from '../components/TabHeaderCanonicalLink.js';
import { HelpLink } from '../components/HelpLink.js';
import { EditLockToggle } from '../components/EditLockToggle.js';
import { CleanRegistryModal } from '../components/CleanRegistryModal.js';
import { ToolRegistryUploadButton } from '../components/ToolRegistryUploadButton.js';
import { DataSourcesTable } from '../components/DataSourcesTable.js';
import { BookmarksTable } from '../components/BookmarksTable.js';
import { ToolActivityTab } from '../components/ToolActivityTab.js';

interface ToolRow {
  id: string;
  type: string;
  nestName: string | null;
  registryContainerId: string;
  registryContainerSlug: string | null;
  registryPath: string;
  // DSR 1.2.0 — storage block in manifest shape; feed to
  // resolveRegistryFiles() for the tab list. Optional so the page
  // degrades to the legacy single-file shape against an older API.
  storage?: {
    containerName: string | null;
    registryPath: string | null;
    registryFiles: RegistryFileEntry[] | null;
  };
}

interface RegistryPointer {
  toolId: string;
  registryPath: string;
  documentId?: string;
  contentUrl?: string;
  contents?: null;
  note?: string;
}

// Operator preference: reload lands you back on your tab.
const ACTIVE_TAB_STORAGE_KEY = 'dsr:activeTab';

// CTSK-290 — synthetic Activity tab, appended AFTER the file-driven
// tabs (same pattern as Timeline Lane's synthetic viz tab). Not a
// registryFiles[] entry; the slug never collides with a real path
// (paths are file names, this is a marker token).
const ACTIVITY_TAB_SLUG = '__activity__';
const ACTIVITY_TAB_LABEL = 'Activity';

function loadStoredTab(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeTab(path: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, path);
  } catch {
    // Private-mode / quota failures are non-fatal — the tab just
    // won't persist across reloads.
  }
}

// ── Component ─────────────────────────────────────────────────────────

export function DataSourcesRegistry({ onNavigate }: { onNavigate: (p: string) => void }) {
  // 1. Find the installed tool by type. If multiple installs exist
  //    (one per nest), the operator picks via a dropdown.
  const toolsQ = useQuery({
    queryKey: ['tools', { type: 'data-sources-registry' }],
    queryFn: () => apiList<ToolRow>('/tools', { query: { type: 'data-sources-registry' } }),
  });
  const tools = toolsQ.data?.data ?? [];
  const [selectedToolId, setSelectedToolId] = useState<string>('');
  useEffect(() => {
    if (!selectedToolId && tools.length > 0) setSelectedToolId(tools[0]!.id);
  }, [tools, selectedToolId]);
  const tool = tools.find((t) => t.id === selectedToolId);

  // 2. Resolve the registered file list → tab strip. Works for both
  //    config shapes via the shared helper; older API responses
  //    without `storage` degrade to the legacy single registryPath.
  const files = useMemo<RegistryFileEntry[]>(
    () => (tool ? resolveRegistryFiles(tool.storage ?? { registryPath: tool.registryPath }) : []),
    [tool],
  );

  // 3. Active tab — keyed by file path, persisted to localStorage so
  //    a reload lands the operator back on their tab. A stored path
  //    that no longer matches a registered file falls back to the
  //    first entry.
  //
  //    CTSK-364 — a `?file=<path>` query param wins over the stored
  //    tab for the INITIAL selection. This is the deep-link half of
  //    `{captain_tasko_ref}` (external tools link back to
  //    /tools/data-sources-registry?file=bookmarks.yaml&entry=<id>);
  //    `entry` is accepted-but-unused for now (row highlight is a
  //    follow-up).
  const [activeTab, setActiveTab] = useState<string | null>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('file');
    return fromUrl ?? loadStoredTab();
  });
  const isActivityTab = activeTab === ACTIVITY_TAB_SLUG;
  const activeFile = isActivityTab
    ? null
    : files.find((f) => f.path === activeTab) ?? files[0] ?? null;

  // CTSK-256 — per-tab edit lock (same CTSK-268 slice B affordance the
  // Timeline Lane tabs carry). Default LOCKED; unlocking reveals the
  // Clean button. Never persisted — a reload or tab switch re-locks.
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [showCleanModal, setShowCleanModal] = useState(false);

  const selectTab = (path: string) => {
    setActiveTab(path);
    storeTab(path);
    // Unlock is a per-tab, per-visit signal; it never follows you
    // across tabs.
    setEditUnlocked(false);
    setShowCleanModal(false);
  };

  // 4. Resolve the registry pointer for the active tab → document id
  //    → contents url. Keyed by [containerName, path] (via the
  //    container id, which is stable per tool) so React Query dedupes
  //    per file and switching back to a visited tab doesn't re-fetch.
  const containerId = tool?.registryContainerId;
  const ptrQ = useQuery({
    queryKey: ['tool-registry', containerId, activeFile?.path],
    queryFn: () =>
      apiSingle<RegistryPointer>(`/tools/${selectedToolId}/registry`, {
        query: { path: activeFile!.path },
      }),
    enabled: !!selectedToolId && !!activeFile,
  });

  // 5. Fetch the YAML bytes for the active tab.
  const yamlQ = useQuery({
    queryKey: ['tool-registry-content', containerId, activeFile?.path, ptrQ.data?.documentId],
    queryFn: async () => {
      if (!ptrQ.data?.documentId) return '';
      const text = await apiFetch<string>(`/documents/${ptrQ.data.documentId}/content`, { raw: true } as never)
        .catch(async () => {
          // The /content endpoint streams text/* directly — fetch raw.
          const res = await fetch(`/api/v1/documents/${ptrQ.data!.documentId}/content`, {
            credentials: 'include',
          });
          if (!res.ok) throw new Error(`Failed to load registry: ${res.status}`);
          return res.text();
        });
      return typeof text === 'string' ? text : '';
    },
    enabled: !!ptrQ.data?.documentId,
  });

  // ── Render ─────────────────────────────────────────────────────────

  if (toolsQ.isLoading) {
    return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading tool…</div>;
  }
  // "Tool isn't installed" guard is still first — before the tabs.
  if (tools.length === 0) {
    return (
      <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13, maxWidth: 720, margin: 20 }}>
        <strong>The data-sources-registry tool isn't installed in this org.</strong>
        <p style={{ margin: '8px 0 0 0' }}>
          Install it from{' '}
          <button
            type="button"
            onClick={() => onNavigate('/settings?section=tools')}
            style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            Settings → Tools
          </button>{' '}
          using <code>tools/data-sources-registry.tool.json</code>.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em' }}>Data Sources Registry</h1>
          <div style={{ marginTop: 4 }}>
            {/* CTSK-280 — consolidated onto the shared per-tab canonical
                chip (CTSK-268 slice A). Replaces the CTSK-269 inline
                "Canonical source of truth" banner + onNavigate ↗ button
                so DSR and Timeline Lane share one affordance: linked
                chip → /documents/<id> in a NEW tab when the pointer has
                resolved a documentId; plain chip otherwise. */}
            {!isActivityTab && (
              <TabHeaderCanonicalLink
                path={activeFile?.path ?? tool?.registryPath ?? 'data_sources.yaml'}
                documentId={ptrQ.data?.documentId}
              />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* CTSK-355 — tool-detail help link. No DSR-specific help doc
              yet, so this maps to the general tools doc (missing-doc
              set tracked on the ship report for follow-up filing). */}
          {/* CTSK-381 — dedicated DSR help doc (was the generic tools doc). */}
          <HelpLink pageName="Data Sources Registry" slug="data-sources-registry" onNavigate={onNavigate} />
          {/* CTSK-316 — upload-here affordance: standard upload dialog
              with the tool's bound container preselected and filenames
              restricted to the registered registryFiles[].path list. */}
          {tool && (
            <ToolRegistryUploadButton
              toolId={tool.id}
              containerId={tool.registryContainerId}
              containerLabel={tool.registryContainerSlug}
              registryFiles={files}
              activePath={activeFile?.path}
            />
          )}
          {/* Tool picker — only shows when >1 install */}
          {tools.length > 1 && (
            <select
              value={selectedToolId}
              onChange={(e) => setSelectedToolId(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                background: 'var(--surface-raised)',
              }}
            >
              {tools.map((t) => (
                <option key={t.id} value={t.id}>{t.nestName ?? '(no nest)'} · {t.registryContainerSlug}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Tab strip — one per registered registry file */}
      <div
        role="tablist"
        aria-label="Registry files"
        style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border-subtle)', marginBottom: 16, flexWrap: 'wrap' }}
      >
        {files.map((f) => {
          const isActive = !isActivityTab && f.path === activeFile?.path;
          return (
            <button
              key={f.path}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectTab(f.path)}
              title={f.path}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                background: 'none',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent-primary)' : '2px solid transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {f.label}
            </button>
          );
        })}
        {/* CTSK-290 — synthetic Activity tab, LAST after the file-driven
            tabs (same placement rule as Timeline Lane's viz tab): the
            per-tool audit trail from GET /tools/:id/activity. */}
        <button
          type="button"
          role="tab"
          aria-selected={isActivityTab}
          onClick={() => selectTab(ACTIVITY_TAB_SLUG)}
          title="Install / troubleshooting activity log for this tool"
          style={{
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: isActivityTab ? 600 : 400,
            background: 'none',
            border: 'none',
            borderBottom: isActivityTab ? '2px solid var(--accent-primary)' : '2px solid transparent',
            color: isActivityTab ? 'var(--text-primary)' : 'var(--text-muted)',
            cursor: 'pointer',
            marginBottom: -1,
          }}
        >
          {ACTIVITY_TAB_LABEL}
        </button>
        {/* CTSK-256 — right of the tab strip: the CTSK-268 slice B lock
            toggle + the Clean affordance it reveals. The Clean button
            needs a resolved documentId; an un-uploaded file has
            nothing to clean and stays hidden. */}
        {activeFile && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'center', paddingBottom: 4 }}>
            {editUnlocked && ptrQ.data?.documentId && (
              <button
                type="button"
                onClick={() => setShowCleanModal(true)}
                title={`Clean ${activeFile.path} (dedupe / normalize / migrate — preview first)`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-raised)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer' }}
              >
                <Brush size={12} aria-hidden /> Clean
              </button>
            )}
            <EditLockToggle
              locked={!editUnlocked}
              onToggle={() => { setEditUnlocked((u) => !u); setShowCleanModal(false); }}
            />
          </div>
        )}
      </div>

      {/* Per-tab body */}
      {isActivityTab && tool ? (
        <ToolActivityTab toolId={tool.id} />
      ) : !activeFile ? (
        <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          No registry files are configured for this tool. Check <code>storage.registryFiles</code> in the tool manifest.
        </div>
      ) : ptrQ.isLoading || (ptrQ.data?.documentId && yamlQ.isLoading) ? (
        <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading {activeFile.path}…</div>
      ) : ptrQ.data && !ptrQ.data.documentId ? (
        // Missing-file empty state — per tab. The file is registered
        // in the tool config but hasn't been uploaded into the bound
        // container yet.
        <div style={{ padding: 16, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          The <code style={{ fontFamily: 'var(--font-mono)' }}>{activeFile.path}</code> file isn't in this
          container yet. Upload it via the container's file list (or re-run the DSR seed step) — see{' '}
          <code>docs/api-howto.md</code> for the container-upload runbook.
        </div>
      ) : activeFile.schema === 'bookmarks-v1' ? (
        // CTSK-364 — the table is mutable here (inline row editor +
        // fetch-and-map persistence): mutation carries the PATCH
        // route context, the {captain_tasko_ref} builder, and the
        // YAML refetch. defaultSort threads the manifest's
        // registryFiles[].defaultSort (CTSK-361A fold).
        // CTSK-351 — pass the file's rowActions[] so the table can
        // render the manifest-declared per-row action palette. When
        // absent/empty, BookmarksTable hides the whole Actions column
        // (no dead column artifact).
        <BookmarksTable
          yamlText={yamlQ.data ?? ''}
          rowActions={activeFile.rowActions}
          defaultSort={activeFile.defaultSort}
          mutation={tool ? {
            toolId: tool.id,
            filePath: activeFile.path,
            makeEntryRef: (entryId) =>
              `${window.location.origin}/tools/data-sources-registry?file=${encodeURIComponent(activeFile.path)}&entry=${encodeURIComponent(entryId)}`,
            onRefetch: () => { void yamlQ.refetch(); },
          } : undefined}
        />
      ) : (
        <DataSourcesTable
          yamlText={yamlQ.data ?? ''}
          registryPath={activeFile.path}
          containerId={containerId}
          // CTSK-367 — per-row Edit over the CTSK-364 PATCH endpoint.
          mutation={tool ? {
            toolId: tool.id,
            filePath: activeFile.path,
            onRefetch: () => { void yamlQ.refetch(); },
          } : undefined}
        />
      )}

      {/* CTSK-256 — cleaning modal (dedupe / normalize / migrate with a
          mandatory dry-run preview before apply). */}
      {showCleanModal && activeFile && ptrQ.data?.documentId && tool && (
        <CleanRegistryModal
          toolId={tool.id}
          path={activeFile.path}
          onClose={() => setShowCleanModal(false)}
        />
      )}
    </div>
  );
}
